import type { RunState } from "../types";
import { useLocalProfile } from "../../local/useLocalProfile";
import { AvatarPreview } from "../../components/AvatarEditor";

/**
 * Top-of-screen HUD. Three groups:
 *   left  — avatar pill (face + name + level + XP bar)
 *   middle — stumbles, timer, stars, coins (dark navy pills)
 *   right — pause button
 *
 * All wrapped in a container that respects safe-area-insets so iPad notch
 * and home-bar don't overlap controls.
 */
export function HUD({
  state,
  totalDistance,
  onPause,
}: {
  state: RunState;
  totalDistance: number;
  onPause: () => void;
}) {
  const { profile } = useLocalProfile();
  const elapsed = state.finishedAt
    ? state.finishedAt - state.startedAt
    : performance.now() - state.startedAt;
  const totalSeconds = Math.max(0, Math.floor(elapsed / 1000));
  const mm = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const ss = (totalSeconds % 60).toString().padStart(2, "0");

  // Level + XP derived from total coins (every 200 coins = 1 level).
  const coins = profile?.coins ?? 0;
  const level = 1 + Math.floor(coins / 200);
  const xpInLevel = coins % 200;
  const xpPct = (xpInLevel / 200) * 100;

  return (
    <div className="hud2">
      <div className="hud2-row">
        {profile && (
          <div className="avatar-pill">
            <div className="avatar-pill-face" style={{ transform: "scale(0.55)" }}>
              <AvatarPreview cfg={profile.avatarConfig} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="avatar-pill-name">{profile.displayName}</div>
              <div className="avatar-pill-level">Level {level}</div>
              <div className="avatar-pill-xp">
                <span style={{ width: `${xpPct}%` }} />
              </div>
            </div>
          </div>
        )}

        <div className="hud2-stats">
          <div className="hud-pill" aria-label={`${state.stumbles} stumbles`}>
            <span className="hud-icon" style={{ color: "#ff8b42" }}>
              💥
            </span>
            <span>{state.stumbles}</span>
          </div>
          <div className="hud-pill" aria-label="Time">
            <span className="hud-icon">⏱</span>
            <span>
              {mm}:{ss}
            </span>
          </div>
          <div className="hud-pill" aria-label="Score">
            <span className="hud-icon" style={{ color: "#ffd93d" }}>
              ⭐
            </span>
            <span>{state.score}</span>
          </div>
          <div className="hud-pill" aria-label="Coins this run">
            <span className="hud-icon" style={{ color: "#ffd93d" }}>
              🪙
            </span>
            <span>{state.coins}</span>
          </div>
        </div>

        <button
          className="btn btn-blue hud-pause"
          onClick={onPause}
          aria-label={state.paused ? "Resume" : "Pause"}
        >
          {state.paused ? "▶" : "⏸"}
        </button>
      </div>

      {/* Distance / progress bar */}
      <div className="hud2-progress">
        <div
          className="hud2-progress-fill"
          style={{ width: `${Math.min(100, (state.distance / totalDistance) * 100)}%` }}
        />
      </div>
    </div>
  );
}
